import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useLocale, useTranslations } from "use-intl";

import { DropdownIcon, ExternalArrowIcon, StoreIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import { Button } from "@/components/ui/button";
import { buildLocalizedPagePath } from "@/lib/localized-page-path";
import { cn } from "@/lib/utils";
import type { RouterOutputs } from "@/types/rpc";
import { normalizeViewerLocale } from "@garimpa/lib/viewerPreferences";

import { getStoresQueryOptions } from "./stores-query";

type PublicStore = RouterOutputs["deals"]["getStores"][number];

function StoreLogo({ store }: { store: PublicStore }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const hasLogo = Boolean(store.logoUrl && failedUrl !== store.logoUrl);

  return (
    <div
      className={cn(
        "flex size-20 shrink-0 items-center justify-center rounded-2xl bg-white/95 p-3 shadow-[0_18px_30px_-22px_rgba(0,0,0,.8)] sm:size-24",
      )}
      style={{ backgroundColor: hasLogo ? (store.logoBackgroundColor ?? undefined) : undefined }}
    >
      {hasLogo ? (
        <img
          alt={`${store.displayName} logo`}
          className={cn(
            "size-full",
            store.logoFit === "cover" ? "object-cover" : "object-contain",
            store.logoShape === "circle" ? "rounded-full" : "rounded-xl",
          )}
          onError={() => setFailedUrl(store.logoUrl)}
          height={96}
          loading="lazy"
          src={store.logoUrl ?? undefined}
          width={96}
        />
      ) : (
        <StoreIcon className="text-3xl text-neutral-600" decorative />
      )}
    </div>
  );
}

function StoreLinks({ store }: { store: PublicStore }) {
  return (
    <div className="flex flex-wrap gap-2">
      {store.channels.map((channel) => (
        <a
          key={channel.providerId}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-white/15 px-4 text-sm font-bold text-inherit transition hover:bg-white hover:text-[#111422] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
          href={channel.url}
          rel="noopener noreferrer sponsored"
          target="_blank"
        >
          {channel.label}
          <ExternalArrowIcon className="text-base" decorative />
        </a>
      ))}
    </div>
  );
}

export function StoresContent({ monitoredOfferCount }: { monitoredOfferCount: number | null }) {
  const t = useTranslations("shared.stores");
  const locale = normalizeViewerLocale(useLocale());
  const storesQuery = useQuery(getStoresQueryOptions());
  const stores = storesQuery.data ?? [];
  const contactPath = buildLocalizedPagePath(locale, "contact");
  const numberFormat = new Intl.NumberFormat(locale, {
    notation: "compact",
    compactDisplay: "long",
    maximumFractionDigits: 1,
  });
  return (
    <main className="overflow-hidden bg-background">
      <section className="relative isolate overflow-hidden bg-[#111422] px-4 py-14 text-white sm:py-20">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-80"
          style={{
            background:
              "radial-gradient(circle at 8% 5%, rgba(255,139,48,.42), transparent 30%), radial-gradient(circle at 94% 78%, rgba(67,126,255,.32), transparent 28%)",
          }}
        />
        <div className="relative mx-auto max-w-[1200px]">
          <div className="max-w-3xl">
            <dl className="flex flex-wrap gap-x-8 gap-y-5 sm:gap-x-12">
              {storesQuery.data !== undefined ? (
                <div className="flex flex-col">
                  <dt className="mt-2 text-sm font-bold text-white/65">
                    {t("trackedStoresLabel")}
                  </dt>
                  <dd className="order-first text-4xl font-black leading-none tracking-[-0.045em] text-[#f7b34c] sm:text-5xl">
                    {numberFormat.format(stores.length)}
                  </dd>
                </div>
              ) : null}
              {monitoredOfferCount !== null ? (
                <div className="flex flex-col border-l border-white/20 pl-8 sm:pl-12">
                  <dt className="mt-2 text-sm font-bold text-white/65">
                    {t("trackedOffersLabel")}
                  </dt>
                  <dd className="order-first text-4xl font-black leading-none tracking-[-0.045em] text-[#f7b34c] sm:text-5xl">
                    {numberFormat.format(monitoredOfferCount)}
                  </dd>
                </div>
              ) : null}
            </dl>
            <h1 className="mt-7 text-balance text-5xl font-black leading-[0.92] tracking-[-0.045em] sm:text-7xl">
              {t("title")}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">
              {t("description")}
            </p>
            <a
              className="mt-8 inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#f7b34c] px-5 text-sm font-black text-[#241a10] transition hover:bg-[#ffc96d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#111422]"
              href="#storefronts"
            >
              {t("heroCta")}
              <DropdownIcon decorative />
            </a>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/50">{t("heroNote")}</p>
          </div>
        </div>
      </section>

      <section id="storefronts" className="mx-auto max-w-[1200px] scroll-mt-20 px-4 py-14 sm:py-20">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-black tracking-[-0.035em] sm:text-4xl">
            {t("collectionTitle")}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            {t("collectionDescription")}
          </p>
        </div>

        {storesQuery.isError ? (
          <p className="mt-10 rounded-xl border border-border bg-card px-5 py-4 text-sm text-muted-foreground">
            {t("directoryUnavailable")}
          </p>
        ) : null}

        <div className="mt-10 space-y-4">
          {stores.map((store, index) => (
            <article
              key={store.id}
              className={cn(
                "group relative grid overflow-hidden rounded-2xl border border-border bg-card text-card-foreground p-6 shadow-[0_24px_50px_-35px_rgba(0,0,0,.72)] transition duration-500 hover:-translate-y-1 sm:p-8 md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center md:gap-8",
                index % 2 === 1 && "md:ml-12",
              )}
              style={{
                backgroundColor: store.brandColor ?? undefined,
                color: store.textColor ?? undefined,
              }}
            >
              <div
                className="pointer-events-none absolute -right-12 -top-20 size-56 rounded-full border-[24px] border-current opacity-[0.09]"
                aria-hidden="true"
              />
              <StoreLogo store={store} />
              <div className="relative mt-6 md:mt-0">
                <h3 className="mt-2 text-2xl font-black tracking-[-0.03em]">{store.displayName}</h3>
                {store.description ? (
                  <p className="mt-3 max-w-2xl text-sm leading-6 opacity-75">{store.description}</p>
                ) : null}
              </div>
              <div className="relative mt-6 md:mt-0 md:justify-self-end">
                <StoreLinks store={store} />
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-4 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-[#e9e3d6] px-6 py-10 text-[#221d1a] sm:px-10 md:grid md:grid-cols-[1fr_auto] md:items-end md:gap-8">
          <div
            className="absolute -right-12 -top-16 size-64 rounded-full border-[28px] border-[#221d1a]/10"
            aria-hidden="true"
          />
          <div className="relative max-w-xl">
            <h2 className="text-3xl font-black tracking-[-0.04em]">{t("requestTitle")}</h2>
            <p className="mt-3 leading-relaxed text-[#514940]">{t("requestDescription")}</p>
            <p className="mt-4 text-sm leading-relaxed text-[#514940]">{t("requestNote")}</p>
          </div>
          <Button
            className="relative mt-7 min-h-12 bg-[#221d1a] px-5 font-bold text-white hover:bg-[#3a302b] md:mt-0"
            render={
              <Link to={contactPath} search={{ subject: "storeRequest" }}>
                <span />
              </Link>
            }
          >
            {t("requestCta")}
            <ExternalArrowIcon className="ml-2 text-base" decorative />
          </Button>
        </div>
        <p className="mx-auto mt-8 max-w-3xl text-center text-sm leading-6 text-muted-foreground">
          {t("disclaimer")}
        </p>
      </section>
    </main>
  );
}
